import { Component, Input, OnChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, forkJoin, of } from 'rxjs';
import { last, map, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { CibleTerrain, EtatObservation, Observation, ObservationsService } from 'src/app/core/services/entities/observations.service';
import { GedService } from 'src/app/core/services/entities/ged.service';
import { GalerieService, PhotoGalerie } from 'src/app/core/services/entities/galerie.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { nouvelleClientRef } from './client-ref';
import { RoleUtil } from './role.util';

const LIEN: Record<CibleTerrain, string> = {
  volaille: 'LOT_VOLAILLE', pisciculture: 'PISCICULTURE', bassins: 'BASSIN', troupeaux: 'TROUPEAU', cultures: 'CULTURE',
};

export const ETATS_OBSERVATION: { value: EtatObservation; label: string; icone: string; fond: string; couleur: string }[] = [
  { value: 'BON', label: 'Bon', icone: 'check_circle', fond: '#dcfce7', couleur: '#166534' },
  { value: 'A_SURVEILLER', label: 'À surveiller', icone: 'visibility', fond: '#fef3c7', couleur: '#92400e' },
  { value: 'PROBLEME', label: 'Problème', icone: 'error', fond: '#fee2e2', couleur: '#991b1b' },
];

/**
 * « Observations du jour » d'une fiche : état (bon, à surveiller, problème), note, photos (appareil ou Galerie Wanar).
 * Un état autre que « Bon » demande une note ou une photo ; « Problème » prévient les responsables (cloche).
 * Comptable : lecture seule. Suppression : gestionnaire et administrateur, ou l'auteur dans les 24 h (le serveur fait foi).
 */
@Component({
  selector: 'bay-observations',
  template: `
    <section class="card border-0 shadow-sm" style="border-radius:20px;" aria-label="Observations du jour" data-etat="observations">
      <div class="card-body p-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0" style="color:#374151;">Observations du jour</h6>
          <button *ngIf="peutSaisir && !formulaire" type="button" class="btn btn-sm fw-semibold" style="background:#e5eadf;color:#0d631b;border-radius:10px;"
                  (click)="ouvrir()" data-test="nouvelle-observation">
            <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">visibility</span>Observer
          </button>
        </div>

        <form *ngIf="formulaire" class="mb-3 p-3 rounded-4" style="background:#f7fbf0;" (ngSubmit)="enregistrer()" data-etat="form-observation">
          <div class="d-flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="État">
            <button *ngFor="let e of etats" type="button" class="btn btn-sm rounded-pill fw-semibold" role="radio" [attr.aria-checked]="etat === e.value"
                    [style.background]="etat === e.value ? e.fond : '#fff'" [style.color]="e.couleur" [style.border]="'1.5px solid ' + (etat === e.value ? e.couleur : '#e5e7eb')"
                    (click)="etat = e.value" [attr.data-etat-obs]="e.value">
              <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">{{ e.icone }}</span>{{ e.label }}
            </button>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-6 col-md-3">
              <label class="form-label small fw-semibold mb-1" [for]="id + '-date'">Date</label>
              <input [id]="id + '-date'" type="date" class="form-control form-control-sm" [(ngModel)]="date" name="date" [max]="aujourdhui" required>
            </div>
            <div class="col-6 col-md-2">
              <label class="form-label small fw-semibold mb-1" [for]="id + '-heure'">Heure</label>
              <input [id]="id + '-heure'" type="time" class="form-control form-control-sm" [(ngModel)]="heure" name="heure">
            </div>
          </div>
          <label class="form-label small fw-semibold mb-1" [for]="id + '-note'">Note <span *ngIf="etat !== 'BON'" class="text-muted fw-normal">(ou une photo)</span></label>
          <textarea [id]="id + '-note'" class="form-control form-control-sm mb-2" rows="2" maxlength="2000" [(ngModel)]="note" name="note"
                    placeholder="Ce que vous avez vu : comportement, eau, feuilles, plaies…"></textarea>
          <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
            <label class="btn btn-sm btn-outline-secondary rounded-3 mb-0">
              <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">add_a_photo</span>Photo
              <input type="file" accept="image/*" capture="environment" multiple class="d-none" (change)="ajouterFichiers($event)">
            </label>
            <bay-bouton-galerie (fichier)="fichiers.push($event)"></bay-bouton-galerie>
            <span *ngFor="let f of fichiers; let i = index" class="badge rounded-pill text-bg-light border">
              {{ f.name }} <button type="button" class="btn btn-link btn-sm p-0 ms-1" [attr.aria-label]="'Retirer ' + f.name" (click)="fichiers.splice(i, 1)">×</button>
            </span>
          </div>
          <div *ngIf="erreur" class="text-danger small mb-2" role="alert">{{ erreur }}</div>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-sm fw-semibold" style="background:#0d631b;color:#fff;border-radius:10px;" [disabled]="envoi">
              <span *ngIf="envoi" class="spinner-border spinner-border-sm me-1"></span>Enregistrer l'observation
            </button>
            <button type="button" class="btn btn-sm btn-light" (click)="formulaire = false">Annuler</button>
          </div>
        </form>

        <p *ngIf="!chargement && !liste.length" class="text-muted small mb-0">Aucune observation pour l'instant.</p>
        <div *ngFor="let o of liste" class="d-flex gap-2 py-2 border-top" [attr.data-observation]="o.id">
          <span class="badge rounded-pill align-self-start" [style.background]="style(o.etat).fond" [style.color]="style(o.etat).couleur">
            {{ o.etatLibelle || style(o.etat).label }}
          </span>
          <div class="flex-grow-1 min-w-0">
            <div class="small text-muted">{{ o.date | date:'dd/MM/yyyy' }}<span *ngIf="o.heure"> à {{ o.heure }}</span><span *ngIf="o.createdBy"> · {{ o.createdBy }}</span></div>
            <div *ngIf="o.note" class="small" style="white-space:pre-line;">{{ o.note }}</div>
            <div *ngIf="o.photosRecues?.length" class="d-flex gap-1 mt-1">
              <button *ngFor="let p of o.photosRecues; let i = index" type="button" class="bay-obs-vignette" (click)="voir(o, i)"
                      [attr.aria-label]="'Voir la photo ' + (i + 1)">
                <img *ngIf="(vignette(p.id) | async) as s" [src]="s" alt="">
              </button>
            </div>
          </div>
          <button *ngIf="peutSupprimer(o)" type="button" class="btn btn-sm btn-link text-danger p-0 align-self-start" (click)="supprimer(o)"
                  aria-label="Supprimer l'observation">
            <span class="material-symbols-outlined" style="font-size:18px;" aria-hidden="true">delete</span>
          </button>
        </div>
      </div>
      <bay-visionneuse *ngIf="photosVues" [photos]="photosVues" [index]="indexVu" (indexChange)="indexVu = $event" (fermer)="photosVues = null"></bay-visionneuse>
    </section>
  `,
  styles: [`
    .bay-obs-vignette { width:56px; height:56px; border:none; padding:0; border-radius:8px; overflow:hidden; background:#f3f4f6; }
    .bay-obs-vignette img { width:100%; height:100%; object-fit:cover; }
  `]
})
export class ObservationsComponent implements OnChanges {
  @Input() cible!: CibleTerrain;
  @Input() cibleId?: number | null;
  @Input() cibleNom = '';

  readonly etats = ETATS_OBSERVATION;
  readonly id = 'obs-' + Math.random().toString(36).slice(2, 7);
  readonly peutSaisir = !RoleUtil.estComptable() && !RoleUtil.estContenu();
  readonly aujourdhui = this.jourLocal();

  liste: Observation[] = [];
  chargement = false;
  formulaire = false;
  envoi = false;
  erreur: string | null = null;
  etat: EtatObservation = 'BON';
  date = this.aujourdhui;
  heure = '';
  note = '';
  fichiers: File[] = [];
  photosVues: PhotoGalerie[] | null = null;
  indexVu = 0;
  private clientRef = nouvelleClientRef();
  private vignettes = new Map<number, Observable<SafeUrl | null>>();

  constructor(private service: ObservationsService, private ged: GedService, private galerie: GalerieService,
              private fermeState: FermeStateService, private sanitizer: DomSanitizer) {}

  ngOnChanges(): void { this.charger(); }

  charger(): void {
    if (!this.cible || !this.cibleId) { return; }
    this.chargement = true;
    this.service.observations(this.cible, this.cibleId).subscribe({
      next: l => { this.liste = l || []; this.chargement = false; },
      error: () => { this.liste = []; this.chargement = false; }
    });
  }

  style(e: EtatObservation) { return this.etats.find(x => x.value === e) || this.etats[0]; }

  ouvrir(): void {
    this.formulaire = true;
    this.etat = 'BON'; this.date = this.jourLocal(); this.note = ''; this.fichiers = []; this.erreur = null;
    const d = new Date();
    this.heure = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    this.clientRef = nouvelleClientRef();
  }

  ajouterFichiers(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.fichiers.push(...Array.from(input.files || []));
    input.value = '';
  }

  enregistrer(): void {
    this.erreur = null;
    if (!this.date) { this.erreur = 'Indiquez la date.'; return; }
    if (this.etat !== 'BON' && !this.note.trim() && !this.fichiers.length) {
      this.erreur = 'Pour « À surveiller » ou « Problème », ajoutez une note ou une photo.';
      return;
    }
    this.envoi = true;
    // Photos : envoi GED lié à la fiche, sous le nom photo-{uuid}.jpg repris dans l'observation
    const noms = this.fichiers.map(() => `photo-${nouvelleClientRef()}.jpg`);
    const envois = this.fichiers.map((f, i) => this.ged.televerser(this.fermeState.fermeId, f, noms[i], {
      type: 'PHOTO', dossierCode: 'PHOTOS_FERME', lienType: LIEN[this.cible], lienId: this.cibleId!, lienLabel: this.cibleNom, nom: noms[i],
    }).pipe(last()));
    (envois.length ? forkJoin(envois) : of([])).pipe(
      switchMap(() => this.service.ajouterObservation(this.cible, this.cibleId!, {
        clientRef: this.clientRef, fermeId: this.fermeState.fermeId, date: this.date, heure: this.heure || undefined,
        etat: this.etat, note: this.note.trim() || undefined, photos: noms,
      }))
    ).subscribe({
      next: o => {
        this.envoi = false; this.formulaire = false;
        this.liste = [o, ...this.liste.filter(x => x.id !== o.id)];
      },
      error: e => { this.envoi = false; this.erreur = e?.error?.message || 'L\'observation n\'a pas pu être enregistrée. Réessayez.'; }
    });
  }

  peutSupprimer(o: Observation): boolean {
    if (!this.peutSaisir) { return false; }
    return RoleUtil.peutModifierSaisieModule({ createdBy: o.createdBy, createdAt: o.createdAt });
  }

  supprimer(o: Observation): void {
    Swal.fire({ title: 'Supprimer cette observation ?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626',
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler' }).then(r => {
      if (!r.isConfirmed) { return; }
      this.service.supprimerObservation(this.cible, o.id!).subscribe({
        next: () => this.liste = this.liste.filter(x => x.id !== o.id),
        error: e => Swal.fire({ icon: 'error', title: 'Suppression impossible', text: e?.error?.message || 'Réessayez plus tard.' })
      });
    });
  }

  vignette(id: number): Observable<SafeUrl | null> {
    let v = this.vignettes.get(id);
    if (!v) { v = this.galerie.miniature(id).pipe(map(u => u ? this.sanitizer.bypassSecurityTrustUrl(u) : null)); this.vignettes.set(id, v); }
    return v;
  }

  voir(o: Observation, i: number): void {
    this.photosVues = (o.photosRecues || []).map(p => ({
      id: p.id, nom: p.nom, datePrise: `${o.date}T${o.heure || '00:00'}:00`, datePriseSource: 'PRISE' as const,
      auteur: o.createdBy ? { nom: o.createdBy } : null, legende: o.note,
    }));
    this.indexVu = i;
  }

  private jourLocal(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
